import { DEFAULT_EMAIL_BODY_TEMPLATE } from "@/lib/settings/notify-email-body-template";
import { DEFAULT_UPLOAD_LIMIT_MB } from "@/lib/upload/resumable-constants";
import { socialConfigReady, type SocialProvider } from "@/lib/auth/social/config";
import { getEmailTemplateId } from "@/lib/settings/notify-email";
import {
  DEFAULT_UPLOAD_EXTENSIONS,
  DEFAULT_UPLOAD_MAX_SIZE_MB,
  UPLOAD_CATEGORY_KINDS,
  type UploadCategoryKind,
} from "@/lib/upload/kinds";
import {
  DEFAULT_UPLOAD_DIR_RULE,
  DEFAULT_UPLOAD_NAME_RULE,
} from "@/lib/upload/dir-rule";
import { generateRandomKey } from "@/lib/random-string";
import { DEFAULT_SITE_SKIN } from "@/config/site-skin";
import { DEFAULT_SITE_TIME_ZONE } from "@/lib/datetime/site-timezone";
import { DEFAULT_TITLE_SEPARATOR } from "@/lib/site/page-title";
import type { ResourceSettings } from "@/lib/video/resource-settings";

export {
  DEFAULT_SITE_TIME_ZONE,
  SITE_TIME_ZONE_OPTIONS,
  getSiteTimeZoneLabel,
  isSiteTimeZone,
} from "@/lib/datetime/site-timezone";

export {
  DEFAULT_RANDOM_LENGTH,
  generateRandomKey,
  generateRandomSymbolKey,
  randomKeyPattern,
  randomSymbolKeyPattern,
} from "@/lib/random-string";

export type UpdateRemindMode = "popup" | "admin_only" | "off";

export type SiteTemplateMode = "responsive" | "adaptive" | "separate";

export const SITE_TEMPLATE_MODE_OPTIONS: {
  value: SiteTemplateMode;
  label: string;
}[] = [
  { value: "responsive", label: "自适应" },
  { value: "adaptive", label: "代码适配" },
  { value: "separate", label: "独立站点" },
];

export function isSiteTemplateMode(value: unknown): value is SiteTemplateMode {
  return (
    value === "responsive" || value === "adaptive" || value === "separate"
  );
}

export function resolveSiteTemplateMode(
  raw: unknown,
  legacyMobileDomainEnabled: boolean,
): SiteTemplateMode {
  if (isSiteTemplateMode(raw)) {
    return raw;
  }

  return legacyMobileDomainEnabled ? "separate" : "responsive";
}

export function normalizeMobileDomain(value: string): string {
  return value
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/[/?#].*$/, "")
    .replace(/\.+$/, "")
    .toLowerCase();
}

export function isValidMobileDomain(value: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/.test(
    value,
  );
}

export type SiteSettings = {
  siteOpen: boolean;

  searchEnabled: boolean;
  siteShortName: string;
  siteLogo: string;
  adminLogo: string;
  siteUrl: string;

  cookieDomain: string;
  copyright: string;

  siteDescription: string;
  icpHtml: string;
  psbHtml: string;

  footerAbout: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;

  contactQrImage: string;

  contactQrCaption: string;
};

export type GlobalSiteSettings = Pick<
  SiteSettings,
  "siteOpen" | "searchEnabled" | "siteUrl" | "cookieDomain" | "adminLogo"
>;

export const MAP_PROVIDERS = ["none", "amap", "bmap"] as const;
export type MapProvider = (typeof MAP_PROVIDERS)[number];

export const MAP_PROVIDER_LABEL: Record<MapProvider, string> = {
  none: "不使用",
  amap: "高德地图",
  bmap: "百度地图",
};

export function resolveMapCredentials(core: {
  mapProvider: MapProvider;
  amapKey: string;
  amapSecret: string;
  bmapKey: string;
}): { provider: MapProvider; key: string; secret: string } {
  if (core.mapProvider === "amap") {
    return { provider: "amap", key: core.amapKey, secret: core.amapSecret };
  }
  if (core.mapProvider === "bmap") {

    return { provider: "bmap", key: core.bmapKey, secret: "" };
  }

  return { provider: "none", key: "", secret: "" };
}

export function normalizeMapProvider(value: unknown): MapProvider {
  return MAP_PROVIDERS.includes(value as MapProvider)
    ? (value as MapProvider)
    : "none";
}

export type CoreSettings = {
  updateRemind: UpdateRemindMode;

  timeZone: string;

  siteSkin: string;
  templateMode: SiteTemplateMode;

  mobileDomain: string;
  pcToMobileRedirect: boolean;

  mapProvider: MapProvider;

  amapKey: string;

  amapSecret: string;

  bmapKey: string;
};

export type SystemEntrySettings = {

  adminPath: string;

  adminGateKey: string;
  /** 只读：是否已完成安装 */
  appInstalled: boolean;
};

export const DEFAULT_SYSTEM_ENTRY_SETTINGS: SystemEntrySettings = {
  adminPath: "admin",
  adminGateKey: "",
  appInstalled: false,
};

export const SITE_URL_PROTOCOLS = ["http://", "https://"] as const;

export type SiteUrlProtocol = (typeof SITE_URL_PROTOCOLS)[number];

export const SITE_URL_PROTOCOL_OPTIONS = SITE_URL_PROTOCOLS.map((value) => ({
  value,
  label: value,
}));

export function splitSiteUrl(raw: string | null | undefined): {
  protocol: SiteUrlProtocol;
  address: string;
} {
  const trimmed = (raw ?? "").trim();
  const matched = /^(https?:\/\/)(.*)$/i.exec(trimmed);

  if (!matched) {
    return { protocol: "http://", address: trimmed };
  }

  return {
    protocol: matched[1].toLowerCase() === "https://" ? "https://" : "http://",
    address: matched[2],
  };
}

export function buildSiteUrl(
  protocol: SiteUrlProtocol,
  address: string,
): string {
  return `${protocol}${address.trim().replace(/^\/+/, "")}`;
}

export function isHttpsSiteUrl(raw: string | null | undefined): boolean {
  return splitSiteUrl(raw).protocol === "https://";
}

export const ADMIN_PATH_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

export const RESERVED_ADMIN_PATHS = [
  "api",
  "install",
  "uploads",
  "_next",
  "favicon.ico",
] as const;

export function normalizeAdminPathValue(raw: string | null | undefined): string {
  return (raw ?? "").trim().replace(/^\/+|\/+$/g, "") || "admin";
}

export function isReservedAdminPath(value: string): boolean {
  return (RESERVED_ADMIN_PATHS as readonly string[]).includes(
    value.toLowerCase(),
  );
}

export function sanitizeAdminPathInput(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 32);
}

export function sanitizeAdminGateKeyInput(raw: string): string {
  return raw.replace(/[^\x21-\x7e]/g, "").slice(0, 64);
}

export function buildAdminLoginUrl(
  origin: string,
  adminPath: string,
  adminGateKey: string,
): string {
  const base = normalizeAdminPathValue(adminPath);
  const key = normalizeAdminGateKey(adminGateKey);
  const query = key ? `?miyao=${encodeURIComponent(key)}` : "";

  return `${origin.replace(/\/+$/, "")}/${base}/login${query}`;
}

export function normalizeAdminGateKey(raw: string | null | undefined): string {
  return (raw ?? "").trim();
}

export type UploadCategorySettings = {
  localImportPath?: string;

  uploadLimitMb: number;
  extensions: Record<UploadCategoryKind, string[]>;
  maxSizeMb: Record<UploadCategoryKind, number>;

  fileMaxSizeMb: number;
  dirRule: string;

  nameRule: string;

  accessDomain: string;

  storageMode: AttachmentStorageMode;

  uploadImage: ImageConvertProfile;

  generatedImage: ImageConvertProfile;

  personalBaseQuotaMb: number;

  enterpriseBaseQuotaMb: number;
};

export const IMAGE_FORMATS = ["keep", "jpg", "png", "webp"] as const;

export type ImageFormat = (typeof IMAGE_FORMATS)[number];

export const IMAGE_FORMAT_OPTIONS: { value: ImageFormat; label: string }[] = [
  { value: "keep", label: "保持原图" },
  { value: "jpg", label: "JPG" },
  { value: "png", label: "PNG" },
  { value: "webp", label: "WebP" },
];

export type ImageConvertProfile = { format: ImageFormat; quality: number };

export const DEFAULT_IMAGE_CONVERT: ImageConvertProfile = { format: "keep", quality: 100 };

export const VIDEO_WATERMARK_FIELD_OPTIONS = [
  { value: "none", label: "不使用" },
  { value: "name", label: "用户名" },
  { value: "phone", label: "手机号" },
] as const;

export const DEFAULT_VIDEO_WATERMARK_FIELD = "name";

export const DEFAULT_VIDEO_WATERMARK_OPACITY = 30;

export const DEFAULT_BASE_QUOTA_MB = 500;

export type OssConfigEntry = {
  label: string;
  key: string;
  value: string;
};

export type OssProviderType = "local" | "tencent" | "aliyun";

export const DEFAULT_OSS_PROVIDER_TYPE: OssProviderType = "tencent";

export const OSS_PROVIDER_TYPE_LABELS: Record<OssProviderType, string> = {
  local: "本地上传",
  tencent: "腾讯云 COS",
  aliyun: "阿里云 OSS",
};

export const OSS_PROVIDER_TYPE_OPTIONS: {
  value: OssProviderType;
  label: string;
}[] = [
  { value: "tencent", label: OSS_PROVIDER_TYPE_LABELS.tencent },
  { value: "aliyun", label: OSS_PROVIDER_TYPE_LABELS.aliyun },
];

export const OSS_CLOUD_PROVIDER_TYPE_OPTIONS = OSS_PROVIDER_TYPE_OPTIONS;

export function getOssProviderTypeLabel(type: OssProviderType): string {
  return OSS_PROVIDER_TYPE_LABELS[type] ?? type;
}

export const LOCAL_OSS_PROVIDER_ID = "LOCALUPLOAD01";
export const LOCAL_OSS_PROVIDER_NAME = "本地上传";
export const LOCAL_ACCESS_DOMAIN_KEY = "LOCAL_ACCESS_DOMAIN";

export const LOCAL_STORAGE_DIR_KEY = "LOCAL_STORAGE_DIR";

export type OssProviderSettings = {
  id: string;
  name: string;
  enabled: boolean;

  type: OssProviderType;
  config: OssConfigEntry[];
};

export type AttachmentStorageMode = "local" | "oss";

export const ATTACHMENT_STORAGE_MODE_OPTIONS: {
  value: AttachmentStorageMode;
  label: string;
}[] = [
  { value: "local", label: "本地上传" },
  { value: "oss", label: "OSS上传" },
];

export type AttachmentStorageSettings = {
  mode: AttachmentStorageMode;
  providers: OssProviderSettings[];
};

export function getOssConfigValue(config: OssConfigEntry[], key: string) {
  return config.find((entry) => entry.key === key)?.value ?? "";
}

export function pickActiveOssProvider(
  providers: OssProviderSettings[],
): OssProviderSettings | undefined {
  return providers.find((item) => item.enabled);
}

export const OSS_PROVIDER_ID_LENGTH = 13;

export type OssProviderFieldSchema = {
  key: string;
  label: string;
  placeholder?: string;

  required?: boolean;

  secret?: boolean;
};

export const OSS_PROVIDER_FIELD_SCHEMAS: Record<
  OssProviderType,
  OssProviderFieldSchema[]
> = {
  local: [
    {
      key: LOCAL_ACCESS_DOMAIN_KEY,
      label: "访问域名",
      placeholder: "留空则使用站点网址",
    },
    {
      key: LOCAL_STORAGE_DIR_KEY,
      label: "存储目录",
      placeholder: "留空用项目内 storage/uploads；Linux 如 /data/uploads，Windows 如 D:\\uploads",
    },
  ],
  tencent: [
    { key: "COS_SECRET_ID", label: "SecretId", required: true },
    { key: "COS_SECRET_KEY", label: "SecretKey", required: true, secret: true },
    {
      key: "COS_REGION",
      label: "所属地域",
      placeholder: "如 ap-guangzhou",
      required: true,
    },
    {
      key: "COS_BUCKET",
      label: "存储桶",
      placeholder: "如 example-1250000000",
      required: true,
    },
    {
      key: "COS_URL",
      label: "访问域名",
      placeholder: "可选，自定义 CDN 域名，留空用默认域名",
    },
  ],
  aliyun: [
    { key: "OSS_ACCESS_KEY_ID", label: "AccessKeyId", required: true },
    {
      key: "OSS_ACCESS_KEY_SECRET",
      label: "AccessKeySecret",
      required: true,
      secret: true,
    },
    {
      key: "OSS_REGION",
      label: "所属地域",
      placeholder: "如 oss-cn-hangzhou",
      required: true,
    },
    {
      key: "OSS_BUCKET",
      label: "存储桶",
      placeholder: "如 my-bucket",
      required: true,
    },
    {
      key: "OSS_URL",
      label: "访问域名",
      placeholder: "可选，自定义 CDN 域名，留空用默认域名",
    },
  ],
};

export const OSS_PROVIDER_BUCKET_KEY: Record<OssProviderType, string> = {
  local: "",
  tencent: "COS_BUCKET",
  aliyun: "OSS_BUCKET",
};

const OSS_CONFIG_DEFAULT_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(OSS_PROVIDER_FIELD_SCHEMAS).flatMap((fields) =>
    fields.map((field) => [field.key, field.label]),
  ),
);

export function isOssProviderType(value: unknown): value is OssProviderType {
  return (
    value === "local" ||
    value === "tencent" ||
    value === "aliyun"
  );
}

export function createLocalOssProvider(
  domain = "",
  enabled = false,
  dir = "",
): OssProviderSettings {
  return {
    id: LOCAL_OSS_PROVIDER_ID,
    name: LOCAL_OSS_PROVIDER_NAME,
    enabled,
    type: "local",
    config: [
      { label: "访问域名", key: LOCAL_ACCESS_DOMAIN_KEY, value: domain },
      { label: "存储目录", key: LOCAL_STORAGE_DIR_KEY, value: dir },
    ],
  };
}

export function findLocalOssProvider(
  providers: OssProviderSettings[],
): OssProviderSettings | undefined {
  return providers.find((item) => item.type === "local");
}

export function getLocalStorageDir(providers: OssProviderSettings[]): string {
  const local = findLocalOssProvider(providers);

  return local ? getOssConfigValue(local.config, LOCAL_STORAGE_DIR_KEY).trim() : "";
}

export function getLocalAccessDomain(
  providers: OssProviderSettings[],
): string {
  const local = findLocalOssProvider(providers);

  return local
    ? getOssConfigValue(local.config, LOCAL_ACCESS_DOMAIN_KEY).trim()
    : "";
}

export function createDefaultOssConfig(
  type: OssProviderType = DEFAULT_OSS_PROVIDER_TYPE,
): OssConfigEntry[] {
  const schema = OSS_PROVIDER_FIELD_SCHEMAS[type];

  return schema.map((field) => ({
    label: field.label,
    key: field.key,
    value: "",
  }));
}

export function getOssProviderRequiredKeys(type: OssProviderType): string[] {
  return OSS_PROVIDER_FIELD_SCHEMAS[type]
    .filter((field) => field.required)
    .map((field) => field.key);
}

export function isOssProviderConfigured(
  provider: OssProviderSettings,
): boolean {
  if (!isOssProviderType(provider.type)) return false;
  return getOssProviderRequiredKeys(provider.type).every(
    (key) => getOssConfigValue(provider.config, key).trim() !== "",
  );
}

export function getOssProviderBucket(provider: OssProviderSettings): string {
  return getOssConfigValue(
    provider.config,
    OSS_PROVIDER_BUCKET_KEY[provider.type],
  ).trim();
}

export function normalizeOssConfigEntry(
  entry: Record<string, unknown>,
): OssConfigEntry {
  const key = typeof entry.key === "string" ? entry.key : "";
  const label =
    typeof entry.label === "string" && entry.label.trim()
      ? entry.label
      : (OSS_CONFIG_DEFAULT_LABELS[key] ?? "");

  return {
    label,
    key,
    value: typeof entry.value === "string" ? entry.value : "",
  };
}

export const DEFAULT_ATTACHMENT_STORAGE_SETTINGS: AttachmentStorageSettings = {
  mode: "local",
  providers: [],
};

export type StatsSettings = {
  defaultBrowsesMin: number;
  defaultBrowsesMax: number;
  defaultViewsMin: number;
  defaultViewsMax: number;
  defaultDownloadsMin: number;
  defaultDownloadsMax: number;
};

export const REGISTER_VERIFY_OPTIONS = [
  { value: "none", label: "不验证" },
  { value: "manual", label: "后台审核" },
  { value: "email", label: "邮箱验证" },
  { value: "phone", label: "手机验证" },
] as const;
export type RegisterVerifyMode = (typeof REGISTER_VERIFY_OPTIONS)[number]["value"];

export function isRegisterVerifyMode(value: unknown): value is RegisterVerifyMode {
  return REGISTER_VERIFY_OPTIONS.some((option) => option.value === value);
}
export type PasswordRecoveryMode = "email" | "phone" | "both";
export type LoginRedirectMode = "home" | "current" | "member_center" | "custom";
export type SubmissionReviewMode = "auto" | "manual";

export type RegistrationSettings = {
  memberCenterEnabled: boolean;
  registrationEnabled: boolean;
  registerVerify: RegisterVerifyMode;
  passwordRecovery: PasswordRecoveryMode;

  passwordChange?: PasswordRecoveryMode;
  ipRegisterLimitEnabled: boolean;
  ipRegisterLimitHours: number;
  forbiddenUsernames: string;

  termsUrl: string;
  privacyUrl: string;
};

export function formatForbiddenUsernames(value: string | null | undefined) {
  if (!value?.trim()) {
    return "";
  }

  return value
    .split(/[\r\n,，]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .join(",");
}

export function parseForbiddenUsernames(value: string) {
  if (!value.trim()) {
    return "";
  }

  return Array.from(
    new Set(
      value
        .split(/[,，\r\n]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ).join(",");
}

export function formatBlockedWords(value: string | null | undefined) {
  if (!value?.trim()) {
    return "";
  }

  return value
    .split(/[,，]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .join(",");
}

export function parseBlockedWords(value: string) {
  if (!value.trim()) {
    return "";
  }

  return Array.from(
    new Set(
      value
        .split(/[,，]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ).join(",");
}

export function resolveSiteLogoUrl(logo: string | undefined | null) {
  if (!logo) return "";
  const trimmed = logo.trim();

  if (!trimmed) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith("/public/")) {
    return trimmed.slice("/public".length) || "/";
  }

  return trimmed;
}

export type SensitiveOperationProtectionMode = "enabled" | "disabled";

export function isSensitiveOperationProtectionMode(
  value: unknown,
): value is SensitiveOperationProtectionMode {
  return value === "enabled" || value === "disabled";
}

export function isSensitiveOperationProtectionEnabled(
  mode: SensitiveOperationProtectionMode,
): boolean {
  return mode === "enabled";
}

export type MemberDefaultAuthMode = "wechat" | "phone" | "password";

export const MEMBER_ACCESS_LOGIN_KEYS = [
  "memberDefaultLogin", "memberDefaultRegister", "memberPhoneRequired",
  "redirectAfterLogin", "customRedirectUrl",
] as const;
export const SENSITIVE_OPERATION_SETTING_KEYS = ["sensitiveOperationProtection", "sensitiveOperationTtlMinutes"] as const;

export type LoginSettings = {
  memberDefaultLogin: MemberDefaultAuthMode;
  memberDefaultRegister: MemberDefaultAuthMode;
  memberPhoneRequired: boolean;

  sensitiveOperationProtection: SensitiveOperationProtectionMode;

  sensitiveOperationTtlMinutes: number;
  forbidMultipleLoginEnabled: boolean;
  sessionKickedMessage: string;
  adminLoginMaxAttempts: number;
  adminLoginLockoutHours: number;
  adminSessionTimeoutSeconds: number;
  frontendLoginMaxAttempts: number;
  frontendLoginLockoutHours: number;
  sessionTimeoutSeconds: number;
  redirectAfterLogin: LoginRedirectMode;
  customRedirectUrl: string;

  autoBanMaxFailures: number;

  autoBanMaxAccounts: number;

  autoBanTempHours: number;

  autoBanMaxStrikes: number;

  autoBanGrayRetentionDays: number;
};

export type MemberAccessSettings = {
  login: Pick<LoginSettings, (typeof MEMBER_ACCESS_LOGIN_KEYS)[number]>;
  register: RegistrationSettings;
};

export type SocialLoginConfigEntry = {
  label: string;
  key: string;
  value: string;
};

export type SocialLoginChannel = {
  provider?: SocialProvider;
  id: string;
  name: string;
  enabled: boolean;
  adminEnabled?: boolean;
  config: SocialLoginConfigEntry[];
};

export type SocialLoginSettings = SocialLoginChannel[];

export const SOCIAL_LOGIN_CONFIG_ID_LENGTH = 13;

export function isSocialLoginChannelConfigured(
  channel: SocialLoginChannel,
): boolean {
  return socialConfigReady(channel);
}

export type CaptchaProvider =
  | "image"
  | "slider"
  | "drag_slide"
  | "click_text"
  | "click_image";

export type LoginVerifyScene = "login" | "register" | "reset_password";

export type TwoFactorMethod = "email" | "sms" | "totp";

export type TwoFactorScope = "admin" | "all";

export const DEFAULT_CAPTCHA_MAX_ATTEMPTS = 30;

export const DEFAULT_CAPTCHA_LOCKOUT_SECONDS = 1800;

export type LoginVerifySettings = {
  captcha: {
    enabled: boolean;
    provider: CaptchaProvider;
    scenes: LoginVerifyScene[];

    maxAttempts: number;

    lockoutSeconds: number;
  };
  twoFactor: {
    enabled: boolean;
    methods: TwoFactorMethod[];
    scope: TwoFactorScope;
  };
};

export type LoginConfigSettings = {
  social: SocialLoginSettings;
  verify: LoginVerifySettings;
};

export const CAPTCHA_PROVIDER_VALUES: readonly CaptchaProvider[] = [
  "image",
  "slider",
  "drag_slide",
  "click_text",
  "click_image",
];

export const LOGIN_VERIFY_SCENE_VALUES: readonly LoginVerifyScene[] = [
  "login",
  "register",
  "reset_password",
];

export const TWO_FACTOR_METHOD_VALUES: readonly TwoFactorMethod[] = [
  "email",
  "sms",
  "totp",
];

export const TWO_FACTOR_SCOPE_VALUES: readonly TwoFactorScope[] = [
  "admin",
  "all",
];

export type SubmissionSettings = {
  submissionEnabled: boolean;
  reviewMode: SubmissionReviewMode;
  submissionLimitCount: number;
  newUserSubmissionWaitMinutes: number;
};

export type CommentReviewMode = SubmissionReviewMode;

export type CommentSettings = {
  commentEnabled: boolean;
  reviewMode: CommentReviewMode;
  commentLimitCount: number;
  newUserCommentWaitMinutes: number;
};

export type AccessControlSettings = {
  externalLinkWhitelist: string[];
  ipWhitelist: string[];
  ipBlacklist: string[];
  blockedWords: string;
};

import type { PayPageData } from "@/lib/pay/page";
import { DEFAULT_PAY_PAGE_DATA } from "@/lib/pay/page";

export type {
  PayConfigEntry,
  PayMethod,
  PayPageData as PaymentSettings,
  PaySetItem,
} from "@/lib/pay/page";

export { DEFAULT_PAY_PAGE_DATA as DEFAULT_PAYMENT_SETTINGS } from "@/lib/pay/page";

export type NotifyNoticeSettings = {
  content: string;
  updatedAt: string | null;
};

export type NotifyAnnouncementSettings = {
  id: string;
  type: string;
  content: string;
  visible: boolean;
  createdAt: string;
};

export const NOTIFY_ANNOUNCEMENT_TYPE_OPTIONS = [
  { value: "温馨提示", label: "温馨提示" },
  { value: "模型上新", label: "模型上新" },
  { value: "渠道上新", label: "渠道上新" },
  { value: "降价通知", label: "降价通知" },
  { value: "模型性能优化", label: "模型性能优化" },
] as const;

export type InboxNotifySettings = {
  notice: NotifyNoticeSettings;
  announcements: NotifyAnnouncementSettings[];
};

export function createNotifyAnnouncement(): NotifyAnnouncementSettings {
  return {
    id: generateRandomKey(NOTIFY_CONFIG_ID_LENGTH),
    type: "",
    content: "",
    visible: true,
    createdAt: new Date().toISOString(),
  };
}

export type NotifyRecipient = "all" | "admin";

export const NOTIFY_RECIPIENT_OPTIONS = [
  { value: "all", label: "全员" },
  { value: "admin", label: "管理" },
] as const;

export function getNotifyRecipientLabel(value: NotifyRecipient): string {
  return (
    NOTIFY_RECIPIENT_OPTIONS.find((option) => option.value === value)?.label ??
    value
  );
}

export type EmailNotifyTemplate = {
  key: string;

  id?: string;
  kind?: "verification" | "notification";
  expireMinutes?: number;
  name?: string;
  enabled: boolean;
  subject: string;
  body: string;
  recipient: NotifyRecipient;
};

export const BUILT_IN_EMAIL_NOTIFY_TEMPLATE_KEYS = [
  "email_verify",
  "contact_form",
  "order_paid",
  "order_shipped",
  "invoice",
  "daily_subscribe",
  "review_remind",
  "submission_remind",
] as const;

export type EmailNotifyTemplateKey =
  (typeof BUILT_IN_EMAIL_NOTIFY_TEMPLATE_KEYS)[number];

export function isBuiltInEmailNotifyTemplateKey(
  key: string,
): key is EmailNotifyTemplateKey {
  return BUILT_IN_EMAIL_NOTIFY_TEMPLATE_KEYS.includes(
    key as EmailNotifyTemplateKey,
  );
}

export function createCustomEmailNotifyTemplate(): EmailNotifyTemplate {
  const key = `custom_${Date.now()}`;
  return {
    key,
    id: getEmailTemplateId({ key }),
    kind: "notification",
    expireMinutes: 5,
    name: "",
    enabled: false,
    subject: "",
    body: "",
    recipient: "all",
  };
}

export const EMAIL_NOTIFY_TEMPLATE_META: {
  key: EmailNotifyTemplateKey;
  name: string;
  defaultSubject: string;
  defaultBody: string;
  defaultRecipient: NotifyRecipient;
}[] = [
  {
    key: "email_verify",
    name: "邮箱验证",
    defaultSubject: "【发信名称】邮箱验证码",
    defaultBody: "您好！\n\n您的邮箱验证码为：{code}\n请在 {time} 分钟内使用，请勿向他人透露。\n\n如非本人操作，请忽略此邮件。",
    defaultRecipient: "all",
  },
  {
    key: "contact_form",
    name: "留言表单",
    defaultSubject: "【发信名称】您有一条新的留言通知",
    defaultBody: "您有一条新的留言通知",
    defaultRecipient: "all",
  },
  {
    key: "order_paid",
    name: "订单付款",
    defaultSubject: "【发信名称】您的订单已付款成功",
    defaultBody: "您的订单已付款成功",
    defaultRecipient: "all",
  },
  {
    key: "order_shipped",
    name: "订单发货",
    defaultSubject: "【发信名称】您的订单已发货",
    defaultBody: "您的订单已发货",
    defaultRecipient: "all",
  },
  {
    key: "invoice",
    name: "发票信息",
    defaultSubject: "【发信名称】您的发票信息已生成",
    defaultBody: "您的发票信息已生成",
    defaultRecipient: "all",
  },
  {
    key: "daily_subscribe",
    name: "每日订阅",
    defaultSubject: "【发信名称】每日订阅内容已更新",
    defaultBody: "每日订阅内容已更新",
    defaultRecipient: "all",
  },
  {
    key: "review_remind",
    name: "审核提醒",
    defaultSubject: "【发信名称】有待审核内容，请及时处理",
    defaultBody: "有待审核内容，请及时处理",
    defaultRecipient: "admin",
  },
  {
    key: "submission_remind",
    name: "投稿提醒",
    defaultSubject: "【发信名称】有新投稿待处理",
    defaultBody: "有新投稿待处理",
    defaultRecipient: "admin",
  },
];

export type NotifySendLimits = {

  cooldownSeconds: number;

  hourlyLimit: number;

  dailyLimit: number;
};

export const DEFAULT_NOTIFY_SEND_LIMITS: NotifySendLimits = {
  cooldownSeconds: 60,
  hourlyLimit: 5,
  dailyLimit: 20,
};

export type EmailBrandingSettings = {
  senderName: string;
};

export type EmailNotifySettings = {

  bodyTemplate?: string;
  accounts: SmtpAccountSettings[];
  templates: EmailNotifyTemplate[];
  sendLimits: NotifySendLimits;
};

export type SmtpAccountSettings = {
  senderName?: string;
  id: string;
  name: string;
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  adminEmail: string;
};

export const NOTIFY_CONFIG_ID_LENGTH = 13;

export function pickActiveSmtpAccount(
  accounts: SmtpAccountSettings[],
): SmtpAccountSettings | undefined {
  return accounts.find((item) => item.enabled) ?? accounts[0];
}

export function normalizeExclusiveEnabled<T extends { id: string; enabled: boolean }>(
  items: T[],
): T[] {
  if (items.length === 0) {
    return items;
  }

  const enabledItems = items.filter((item) => item.enabled);

  if (enabledItems.length === 1) {
    return items;
  }

  const activeId = enabledItems[0]?.id ?? items[0].id;

  return items.map((item) => ({
    ...item,
    enabled: item.id === activeId,
  }));
}

export function applyExclusiveEnabled<T extends { id: string; enabled: boolean }>(
  items: T[],
  targetId: string,
  enabled: boolean,
): T[] {
  if (items.length === 0) {
    return items;
  }

  if (enabled) {
    return items.map((item) => ({
      ...item,
      enabled: item.id === targetId,
    }));
  }

  const target = items.find((item) => item.id === targetId);

  if (!target?.enabled) {
    return items;
  }

  if (items.length === 1) {
    return items;
  }

  const fallbackId = items.find((item) => item.id !== targetId)?.id ?? items[0].id;

  return items.map((item) => ({
    ...item,
    enabled: item.id === fallbackId,
  }));
}

export type SmsPlatform = "tencent" | "aliyun";

export function isSmsPlatform(value: unknown): value is SmsPlatform {
  return value === "tencent" || value === "aliyun";
}

export const SMS_PLATFORM_OPTIONS = [
  { value: "tencent", label: "腾讯云" },
  { value: "aliyun", label: "阿里云" },
] as const;

export type SmsConfigEntry = {
  label: string;
  key: string;
  value: string;
};

export type SmsPlatformFieldSchema = {
  key: string;
  label: string;
  placeholder?: string;

  defaultValue?: string;

  required?: boolean;

  secret?: boolean;
};

export const SMS_PLATFORM_FIELD_SCHEMAS: Record<
  SmsPlatform,
  SmsPlatformFieldSchema[]
> = {
  tencent: [
    { key: "SECRET_ID", label: "SecretId", required: true },
    { key: "SECRET_KEY", label: "SecretKey", required: true, secret: true },
    {
      key: "SDK_APP_ID",
      label: "SdkAppId",
      placeholder: "如 1400000000",
      required: true,
    },
    {
      key: "REGION",
      label: "所属地域",
      placeholder: "默认 ap-guangzhou",
      defaultValue: "ap-guangzhou",
    },
  ],
  aliyun: [
    { key: "ACCESS_KEY_ID", label: "AccessKeyId", required: true },
    {
      key: "ACCESS_KEY_SECRET",
      label: "AccessKeySecret",
      required: true,
      secret: true,
    },
  ],
};

const SMS_CONFIG_DEFAULT_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(SMS_PLATFORM_FIELD_SCHEMAS).flatMap((fields) =>
    fields.map((field) => [field.key, field.label]),
  ),
);

export function getSmsConfigValue(config: SmsConfigEntry[], key: string) {
  return config.find((entry) => entry.key === key)?.value ?? "";
}

export function createDefaultSmsConfig(
  platform: SmsPlatform = "tencent",
): SmsConfigEntry[] {
  const schema = SMS_PLATFORM_FIELD_SCHEMAS[platform];

  return schema.map((field) => ({
    label: field.label,
    key: field.key,
    value: field.defaultValue ?? "",
  }));
}

export function getSmsPlatformRequiredKeys(platform: SmsPlatform): string[] {
  return SMS_PLATFORM_FIELD_SCHEMAS[platform]
    .filter((field) => field.required)
    .map((field) => field.key);
}

export function isSmsPlatformConfigured(
  platform: SmsPlatformConfigSettings,
): boolean {
  return isSmsPlatform(platform.platform) && getSmsPlatformRequiredKeys(platform.platform).every(
    (key) => getSmsConfigValue(platform.config, key).trim() !== "",
  );
}

export function normalizeSmsConfigEntry(
  entry: Record<string, unknown>,
): SmsConfigEntry {
  const key = typeof entry.key === "string" ? entry.key : "";
  const label =
    typeof entry.label === "string" && entry.label.trim()
      ? entry.label
      : (SMS_CONFIG_DEFAULT_LABELS[key] ?? "");

  return {
    label,
    key,
    value: typeof entry.value === "string" ? entry.value : "",
  };
}

export type SmsNotifyTemplate = {
  key: string;
  name?: string;

  expireMinutes?: number;
  enabled: boolean;

  fields: Record<string, string>;
  recipient: NotifyRecipient;
};

export type SmsTemplateColumn = {
  key: string;
};

export const SMS_TEMPLATE_COLUMN_MIN = 1;
export const SMS_TEMPLATE_COLUMN_MAX = 5;

export const DEFAULT_SMS_TEMPLATE_COLUMNS: SmsTemplateColumn[] = [
  { key: "signName" },
  { key: "templateId" },
  { key: "content" },
];

export function getSmsTemplateFieldValue(
  template: SmsNotifyTemplate,
  key: string,
): string {
  return template.fields[key] ?? "";
}

export const BUILT_IN_SMS_NOTIFY_TEMPLATE_KEYS = [
  "register",
  "bind_phone",
  "login",
  "reset_password",
  "order_shipped",
  "order_paid",
  "contact_form",
] as const;

export type SmsNotifyTemplateKey =
  (typeof BUILT_IN_SMS_NOTIFY_TEMPLATE_KEYS)[number];

export function isBuiltInSmsNotifyTemplateKey(
  key: string,
): key is SmsNotifyTemplateKey {
  return BUILT_IN_SMS_NOTIFY_TEMPLATE_KEYS.includes(key as SmsNotifyTemplateKey);
}

export function createCustomSmsNotifyTemplate(): SmsNotifyTemplate {
  return {
    key: `custom_${Date.now()}`,
    name: "",
    expireMinutes: 5,
    enabled: false,
    fields: {},
    recipient: "all",
  };
}

export const SMS_NOTIFY_TEMPLATE_META: {
  key: SmsNotifyTemplateKey;
  name: string;
  defaultContent: string;
  defaultRecipient: NotifyRecipient;
}[] = [
  {
    key: "register",
    name: "账号注册",
    defaultContent: "您的注册验证码为{code}，请在{time}分钟内填写。",
    defaultRecipient: "all",
  },
  {
    key: "bind_phone",
    name: "手机绑定",
    defaultContent: "您的手机绑定验证码为{code}，请在{time}分钟内填写。",
    defaultRecipient: "all",
  },
  {
    key: "login",
    name: "账号登录",
    defaultContent: "您的登录验证码为{code}，请在{time}分钟内填写。",
    defaultRecipient: "all",
  },
  {
    key: "reset_password",
    name: "找回密码",
    defaultContent: "您的找回密码验证码为{code}，请在{time}分钟内填写。",
    defaultRecipient: "all",
  },
  {
    key: "order_shipped",
    name: "订单发货",
    defaultContent: "您的订单已发货，请注意查收。",
    defaultRecipient: "all",
  },
  {
    key: "order_paid",
    name: "订单付款",
    defaultContent: "有新订单已付款，请及时处理。",
    defaultRecipient: "admin",
  },
  {
    key: "contact_form",
    name: "留言表单",
    defaultContent: "有新的留言待处理，请及时查看。",
    defaultRecipient: "admin",
  },
];

export function createDefaultEmailNotifyTemplates(): EmailNotifyTemplate[] {
  return EMAIL_NOTIFY_TEMPLATE_META.map((meta) => ({
    key: meta.key,
    id: getEmailTemplateId({ key: meta.key }),
    name: meta.name,
    kind: meta.key === "email_verify" ? "verification" : "notification",
    expireMinutes: 5,
    enabled: false,
    subject: meta.defaultSubject,
    body: meta.defaultBody,
    recipient: meta.defaultRecipient,
  }));
}

export function createDefaultSmsNotifyTemplates(): SmsNotifyTemplate[] {
  return SMS_NOTIFY_TEMPLATE_META.map((meta) => ({
    key: meta.key,
    name: meta.name,
    expireMinutes: 5,
    enabled: false,
    fields: { signName: "", templateId: "", content: meta.defaultContent },
    recipient: meta.defaultRecipient,
  }));
}

export type SmsNotifySettings = {
  platforms: SmsPlatformConfigSettings[];
  columns: SmsTemplateColumn[];
  templates: SmsNotifyTemplate[];
  sendLimits: NotifySendLimits;
};

export type SmsPlatformConfigSettings = {
  id: string;
  name: string;
  enabled: boolean;
  platform: SmsPlatform;
  config: SmsConfigEntry[];
  adminPhone: string;
};

export function pickActiveSmsPlatform(
  platforms: SmsPlatformConfigSettings[],
): SmsPlatformConfigSettings | undefined {
  return platforms.find((item) => item.enabled) ?? platforms[0];
}

export type NotifySettings = {
  inbox: InboxNotifySettings;
  email: EmailNotifySettings;
  sms: SmsNotifySettings;
};

export type WechatOATemplate = {
  key: string;
  name?: string;
  enabled: boolean;
  content: string;
  recipient: NotifyRecipient;

  templateId?: string;
  templateAppId?: string;
  url?: string;
};

export const BUILT_IN_WECHAT_OA_TEMPLATE_KEYS = [
  "order_paid",
  "account_login",
] as const;

export type WechatOATemplateKey =
  (typeof BUILT_IN_WECHAT_OA_TEMPLATE_KEYS)[number];

export function isBuiltInWechatOATemplateKey(
  key: string,
): key is WechatOATemplateKey {
  return BUILT_IN_WECHAT_OA_TEMPLATE_KEYS.includes(key as WechatOATemplateKey);
}

export function createCustomWechatOATemplate(): WechatOATemplate {
  return {
    key: `custom_${Date.now()}`,
    name: "",
    enabled: false,
    content: "",
    recipient: "all",
  };
}

export const WECHAT_OA_TEMPLATE_META: {
  key: WechatOATemplateKey;
  name: string;
  defaultRecipient: NotifyRecipient;
}[] = [
  { key: "order_paid", name: "支付订单", defaultRecipient: "all" },
  { key: "account_login", name: "账号登录", defaultRecipient: "all" },
];

export type WechatOAMessagePushSettings = {
  enabled: boolean;
  templates: WechatOATemplate[];
};

export type OfficialAccountSettings = {
  enabled: boolean;
  appId: string;
  appSecret: string;

  verifyFile: string;
  messagePush: WechatOAMessagePushSettings;
};

export type MiniProgramPaymentMethod = "wechatpay" | "alipay" | "balancepay";

export const MINI_PROGRAM_PAYMENT_OPTIONS: {
  value: MiniProgramPaymentMethod;
  label: string;
}[] = [
  { value: "wechatpay", label: "微信支付" },
  { value: "alipay", label: "支付宝支付" },
  { value: "balancepay", label: "余额支付" },
];

export type MiniProgramConfigEntry = {
  label: string;
  key: string;
  value: string;
};

export type MiniProgramSettings = {
  id: string;
  name: string;
  enabled: boolean;
  verifyToken: string;
  config: MiniProgramConfigEntry[];
  paymentEnabled: boolean;
  paymentMethods: MiniProgramPaymentMethod[];
};

export function createDefaultMiniProgramConfig(): MiniProgramConfigEntry[] {
  return [
    { label: "应用 ID", key: "AppID", value: "" },
    { label: "应用密钥", key: "AppSecret", value: "" },
  ];
}

const MINI_PROGRAM_CONFIG_DEFAULT_LABELS: Record<string, string> = {
  AppID: "应用 ID",
  AppSecret: "应用密钥",
};

export function normalizeMiniProgramConfigEntry(
  entry: Record<string, unknown>,
): MiniProgramConfigEntry {
  const key = typeof entry.key === "string" ? entry.key : "";
  const label =
    typeof entry.label === "string" && entry.label.trim()
      ? entry.label
      : (MINI_PROGRAM_CONFIG_DEFAULT_LABELS[key] ?? "");

  return {
    label,
    key,
    value: typeof entry.value === "string" ? entry.value : "",
  };
}

export function getMiniProgramConfigValue(
  config: MiniProgramConfigEntry[],
  key: string,
) {
  return config.find((entry) => entry.key === key)?.value ?? "";
}

export function miniProgramConfigFromLegacyFields(
  appId: string,
  appSecret: string,
): MiniProgramConfigEntry[] {
  return createDefaultMiniProgramConfig().map((entry) => {
    if (entry.key === "AppID") {
      return { ...entry, value: appId };
    }

    if (entry.key === "AppSecret") {
      return { ...entry, value: appSecret };
    }

    return entry;
  });
}

/** @deprecated 小程序配置已合并为 MiniProgramSettings[] */
export type WechatMiniProgramSettings = MiniProgramSettings;

/** @deprecated 小程序配置已合并为 MiniProgramSettings[] */
export type DouyinMiniProgramSettings = MiniProgramSettings;

export type ChannelSettings = {
  officialAccount: OfficialAccountSettings;
  miniPrograms: MiniProgramSettings[];
};

export type UrlMode = "rewrite" | "static";
export type ChannelUrlMode = "directory" | "module";

export type DocumentUrlMode =
  | "module_directory"
  | "simplified_directory"

  | "current_directory"
  | "parent_current_directory";
export type ChannelTitleMode =
  | "channel"
  | "channel_site"
  | "channel_page"
  | "channel_page_site";
export type DocumentTitleMode = "title" | "title_site" | "title_channel_site";
export type SitemapType = "xml" | "txt" | "html";
export type SitemapFilterRule = "hidden_channels" | "external_links";
export type SitemapFrequency =
  | "always"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "never";
export type SitemapPriority =
  | "1.0"
  | "0.9"
  | "0.8"
  | "0.7"
  | "0.6"
  | "0.5"
  | "0.4"
  | "0.3"
  | "0.2"
  | "0.1";

export type PublishCacheOptions = {
  clearHomeCache: boolean;
  clearChannelCache: boolean;
  clearAdjacentCache: boolean;
};

export type SeoUrlSettings = {
  urlMode: UrlMode;
  channelUrlMode: ChannelUrlMode;
  documentUrlMode: DocumentUrlMode;
  channelTitleMode: ChannelTitleMode;
  documentTitleMode: DocumentTitleMode;
  titleSeparator: string;
  publishCache: PublishCacheOptions;
};

export type SeoSitemapSettings = {
  autoUpdate: boolean;
  types: SitemapType[];
  filterRules: SitemapFilterRule[];
  frequency: {
    home: SitemapFrequency;
    list: SitemapFrequency;
    detail: SitemapFrequency;
  };
  priority: {
    home: SitemapPriority;
    list: SitemapPriority;
    detail: SitemapPriority;
  };
  limits: {
    documents: number;
    tags: number;
  };
};

export type LinkGroup = {
  id: string;
  name: string;
  sortOrder: number;
};

export type LinkItem = {
  id: string;
  name: string;
  url: string;
  visible: boolean;
  groupId: string;
  sortOrder: number;
};

export type LinksSettings = {
  groups: LinkGroup[];
  links: LinkItem[];
};

export type SeoSettings = {
  url: SeoUrlSettings;
  sitemap: SeoSitemapSettings;
  links: LinksSettings;
};

export const MINI_PROGRAM_API_KEY_LENGTH = 32;
export const MINI_PROGRAM_ID_LENGTH = 13;

export function createDefaultWechatOATemplates(): WechatOATemplate[] {
  return WECHAT_OA_TEMPLATE_META.map((meta) => ({
    key: meta.key,
    enabled: false,
    content: "",
    recipient: meta.defaultRecipient,
  }));
}

export type CorePageSettings = {
  resources?: ResourceSettings;
  core: CoreSettings;
  entry: SystemEntrySettings;
  stats: StatsSettings;
  register: RegistrationSettings;
  login: LoginSettings;
  submission: SubmissionSettings;
  comment: CommentSettings;
  access: AccessControlSettings;
};

export type BasicPageSettings = {
  site: SiteSettings;
  core: CoreSettings;
  upload: UploadCategorySettings;
  stats: StatsSettings;
  register: RegistrationSettings;
  login: LoginSettings;
  submission: SubmissionSettings;
  access: AccessControlSettings;
  payment: PayPageData;
  notify: NotifySettings;
  channel: ChannelSettings;
};

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteOpen: true,
  searchEnabled: true,
  siteShortName: "",

  siteLogo: "",
  adminLogo: "",
  siteUrl: "http://www.chenbin.cc",
  cookieDomain: "",
  copyright: "",
  siteDescription: "",
  icpHtml:
    '<a href="https://beian.miit.gov.cn/" rel="nofollow" target="_blank">粤ICP备xxxxxxxx号</a>',
  psbHtml:
    '<a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=" rel="nofollow" target="_blank">粤公网安备xxxxxx号</a>',

  footerAbout: "",
  contactEmail: "website@jinzita.vip",
  contactPhone: "17301902290",
  contactAddress: "深圳市龙岗区坂田街道中浩新城电商大厦A座3楼317",
  contactQrImage: "/images/qrcode-sample.jpg",
  contactQrCaption: "微信扫码咨询",
};

export const DEFAULT_CORE_SETTINGS: CoreSettings = {
  updateRemind: "admin_only",
  timeZone: DEFAULT_SITE_TIME_ZONE,
  siteSkin: DEFAULT_SITE_SKIN,
  templateMode: "responsive",
  mobileDomain: "",
  pcToMobileRedirect: false,
  mapProvider: "none",
  amapKey: "",
  amapSecret: "",
  bmapKey: "",
};

export const DEFAULT_UPLOAD_CATEGORY_SETTINGS: UploadCategorySettings = {
  localImportPath: "",
  uploadLimitMb: DEFAULT_UPLOAD_LIMIT_MB,
  extensions: DEFAULT_UPLOAD_EXTENSIONS,
  maxSizeMb: Object.fromEntries(
    UPLOAD_CATEGORY_KINDS.map((kind) => [kind, DEFAULT_UPLOAD_MAX_SIZE_MB]),
  ) as Record<UploadCategoryKind, number>,
  fileMaxSizeMb: DEFAULT_UPLOAD_MAX_SIZE_MB,
  dirRule: DEFAULT_UPLOAD_DIR_RULE,
  nameRule: DEFAULT_UPLOAD_NAME_RULE,
  accessDomain: "",
  storageMode: "local",
  uploadImage: DEFAULT_IMAGE_CONVERT,
  generatedImage: DEFAULT_IMAGE_CONVERT,
  personalBaseQuotaMb: DEFAULT_BASE_QUOTA_MB,
  enterpriseBaseQuotaMb: DEFAULT_BASE_QUOTA_MB,
};

export const DEFAULT_STATS_SETTINGS: StatsSettings = {
  defaultBrowsesMin: 1,
  defaultBrowsesMax: 100,
  defaultViewsMin: 1,
  defaultViewsMax: 100,
  defaultDownloadsMin: 1,
  defaultDownloadsMax: 100,
};

export const DEFAULT_REGISTRATION_SETTINGS: RegistrationSettings = {
  memberCenterEnabled: true,
  registrationEnabled: true,
  registerVerify: "phone",
  passwordRecovery: "both",
  ipRegisterLimitEnabled: false,
  ipRegisterLimitHours: 24,
  forbiddenUsernames: "admin,root,test",
  termsUrl: "",
  privacyUrl: "",
};

export const DEFAULT_LOGIN_SETTINGS: LoginSettings = {
  memberDefaultLogin: "password",
  memberDefaultRegister: "password",
  memberPhoneRequired: true,
  sensitiveOperationProtection: "enabled",
  sensitiveOperationTtlMinutes: 30,
  forbidMultipleLoginEnabled: false,
  sessionKickedMessage: "您已被挤下线，如非本人操作，请及时修改密码。",
  adminLoginMaxAttempts: 5,
  adminLoginLockoutHours: 1,
  adminSessionTimeoutSeconds: 86400,
  frontendLoginMaxAttempts: 5,
  frontendLoginLockoutHours: 1,
  sessionTimeoutSeconds: 86400,
  redirectAfterLogin: "home",
  customRedirectUrl: "",
  autoBanMaxFailures: 50,
  autoBanMaxAccounts: 8,
  autoBanTempHours: 24,
  autoBanMaxStrikes: 3,
  autoBanGrayRetentionDays: 30,
};

export function createSocialLoginChannel(): SocialLoginChannel {
  return {
    id: generateRandomKey(SOCIAL_LOGIN_CONFIG_ID_LENGTH),
    name: "",
    provider: "wechat",
    enabled: false,
    adminEnabled: false,
    config: createSocialLoginCredentialEntries("AppID", "AppSecret"),
  };
}

function createSocialLoginCredentialEntries(
  idLabel: string,
  secretLabel: string,
): SocialLoginConfigEntry[] {
  return [
    { label: idLabel, key: "appId", value: "" },
    { label: secretLabel, key: "appSecret", value: "" },
    { label: "回调地址", key: "redirectUri", value: "" },
  ];
}

export function createDefaultSocialLoginSettings(): SocialLoginSettings {

  const defaults: {
    id: string;
    name: string;
    idLabel: string;
    secretLabel: string;
  }[] = [
    { id: "wechatlogin", name: "微信登录", idLabel: "AppID", secretLabel: "AppSecret" },
    { id: "qqlogin", name: "QQ登录", idLabel: "APP ID", secretLabel: "APP Key" },
    { id: "douyinlogin", name: "抖音登录", idLabel: "Client Key", secretLabel: "Client Secret" },
  ];

  return defaults.map(({ id, name, idLabel, secretLabel }) => ({
    id,
    name,
    enabled: false,
    adminEnabled: false,
    config: createSocialLoginCredentialEntries(idLabel, secretLabel),
  }));
}

export const DEFAULT_LOGIN_CONFIG_SETTINGS: LoginConfigSettings = {
  social: createDefaultSocialLoginSettings(),
  verify: {
    captcha: {
      enabled: false,
      provider: "image",
      scenes: ["login"],
      maxAttempts: DEFAULT_CAPTCHA_MAX_ATTEMPTS,
      lockoutSeconds: DEFAULT_CAPTCHA_LOCKOUT_SECONDS,
    },
    twoFactor: {
      enabled: false,
      methods: [],
      scope: "admin",
    },
  },
};

export const DEFAULT_SUBMISSION_SETTINGS: SubmissionSettings = {
  submissionEnabled: true,
  reviewMode: "manual",
  submissionLimitCount: 10,
  newUserSubmissionWaitMinutes: 30,
};

export const DEFAULT_COMMENT_SETTINGS: CommentSettings = {
  commentEnabled: true,
  reviewMode: "manual",
  commentLimitCount: 50,
  newUserCommentWaitMinutes: 30,
};

export const DEFAULT_ACCESS_CONTROL_SETTINGS: AccessControlSettings = {
  externalLinkWhitelist: [],
  ipWhitelist: [],
  ipBlacklist: [],
  blockedWords: "",
};

export const DEFAULT_NOTIFY_SETTINGS: NotifySettings = {
  inbox: {
    notice: { content: "", updatedAt: null },
    announcements: [],
  },
  email: {
    bodyTemplate: DEFAULT_EMAIL_BODY_TEMPLATE,
    accounts: [],
    templates: createDefaultEmailNotifyTemplates(),
    sendLimits: { ...DEFAULT_NOTIFY_SEND_LIMITS },
  },
  sms: {
    platforms: [],
    columns: [...DEFAULT_SMS_TEMPLATE_COLUMNS],
    templates: createDefaultSmsNotifyTemplates(),
    sendLimits: { ...DEFAULT_NOTIFY_SEND_LIMITS },
  },
};

export const DEFAULT_CHANNEL_SETTINGS: ChannelSettings = {
  officialAccount: {
    enabled: false,
    appId: "",
    appSecret: "",
    verifyFile: "",
    messagePush: {
      enabled: false,
      templates: createDefaultWechatOATemplates(),
    },
  },
  miniPrograms: [],
};

export const DEFAULT_SEO_SETTINGS: SeoSettings = {
  url: {
    urlMode: "rewrite",
    channelUrlMode: "directory",
    documentUrlMode: "parent_current_directory",
    channelTitleMode: "channel_page_site",
    documentTitleMode: "title_site",
    titleSeparator: DEFAULT_TITLE_SEPARATOR,
    publishCache: {
      clearHomeCache: false,
      clearChannelCache: false,
      clearAdjacentCache: false,
    },
  },
  sitemap: {
    autoUpdate: true,
    types: ["xml"],
    filterRules: ["hidden_channels", "external_links"],
    frequency: {
      home: "always",
      list: "hourly",
      detail: "daily",
    },
    priority: {
      home: "1.0",
      list: "0.8",
      detail: "0.6",
    },
    limits: {
      documents: 100,
      tags: 100,
    },
  },
  links: {
    groups: [
      {
        id: "default",
        name: "默认分组",
        sortOrder: 100,
      },
    ],
    links: [],
  },
};

export const DEFAULT_CORE_PAGE_SETTINGS: CorePageSettings = {
  core: DEFAULT_CORE_SETTINGS,
  entry: DEFAULT_SYSTEM_ENTRY_SETTINGS,
  stats: DEFAULT_STATS_SETTINGS,
  register: DEFAULT_REGISTRATION_SETTINGS,
  login: DEFAULT_LOGIN_SETTINGS,
  submission: DEFAULT_SUBMISSION_SETTINGS,
  comment: DEFAULT_COMMENT_SETTINGS,
  access: DEFAULT_ACCESS_CONTROL_SETTINGS,
};

export const DEFAULT_BASIC_PAGE_SETTINGS: BasicPageSettings = {
  site: DEFAULT_SITE_SETTINGS,
  core: DEFAULT_CORE_SETTINGS,
  upload: DEFAULT_UPLOAD_CATEGORY_SETTINGS,
  stats: DEFAULT_STATS_SETTINGS,
  register: DEFAULT_REGISTRATION_SETTINGS,
  login: DEFAULT_LOGIN_SETTINGS,
  submission: DEFAULT_SUBMISSION_SETTINGS,
  access: DEFAULT_ACCESS_CONTROL_SETTINGS,
  payment: DEFAULT_PAY_PAGE_DATA,
  notify: DEFAULT_NOTIFY_SETTINGS,
  channel: DEFAULT_CHANNEL_SETTINGS,
};

export function pickRandomInRange(min: number, max: number) {
  const low = Math.min(min, max);
  const high = Math.max(min, max);

  return Math.floor(Math.random() * (high - low + 1)) + low;
}

export function pickDefaultBrowses(stats: StatsSettings) {
  return pickRandomInRange(stats.defaultBrowsesMin, stats.defaultBrowsesMax);
}

export function pickDefaultViews(stats: StatsSettings) {
  return pickRandomInRange(stats.defaultViewsMin, stats.defaultViewsMax);
}

export function pickDefaultDownloads(stats: StatsSettings) {
  return pickRandomInRange(stats.defaultDownloadsMin, stats.defaultDownloadsMax);
}
